// Sprint chatgpt-message-rail (D1-D3, D5) — pure selectors backing the
// right-edge message rail. No DOM, no React: `MessageRail.tsx` /
// `useMessageRail.ts` are the only callers. Kept pure so the ≥10 anchored
// cases in `tests/lib/message-rail.test.ts` run without mounting anything.

import type { Message } from '../types';
import { asUserContent, isFounderOrigin } from './messages';

// D2 — the rail does not mount below this many founder messages (Santi
// 2026-09-10: "a partir del tercer mensaje, no desde el segundo").
export const RAIL_MIN_MESSAGES = 3;

// D3 — above this count, messages bucket into a fixed number of bars
// instead of one bar each.
export const RAIL_MAX_BARS = 8;

// D5 — preview text is trimmed to this length BEFORE it reaches the DOM
// (the `truncate` CSS class only clips visually; this bounds the string
// itself so a multi-thousand-character dictation never lands in a `<li>`).
export const RAIL_PREVIEW_MAX_CHARS = 120;

// D1 — "mensaje mío" = `type === 'user'` AND founder-authored. Reuses the
// single-owner predicate `isFounderOrigin` (moved here from
// `UserMessage.tsx` by this sprint, D1). Does not redefine turn boundaries
// — `groupIntoTurns` keeps cutting on every `type='user'` row regardless
// of origin; this is only the rail's own index.
export function founderMessagesOf(messages: Message[]): Message[] {
  return messages.filter((m) => m.type === 'user' && isFounderOrigin(m.origin));
}

// D3 — which bar (0-based, `< min(n, cap)`) message index `i` (0-based, of
// `n` total founder messages) falls into. `n <= cap` → one bar per
// message, i.e. the identity mapping. `n > cap` → `floor(i * cap / n)`.
export function bucketOf(i: number, n: number, cap: number = RAIL_MAX_BARS): number {
  if (n <= cap) return i;
  return Math.floor((i * cap) / n);
}

// D5 — first non-empty line of the message's user content, trimmed and
// capped at `RAIL_PREVIEW_MAX_CHARS`. Returns `null` for an attachment-only
// message (no non-empty line) — callers fall back to
// `t('messageRail.attachmentOnly')`; kept out of this pure module so it
// has no i18n dependency.
export function previewOf(message: Message): string | null {
  const text = asUserContent(message).text;
  const firstLine = text.split('\n').find((line) => line.trim().length > 0);
  if (!firstLine) return null;
  const trimmed = firstLine.trim();
  return trimmed.length > RAIL_PREVIEW_MAX_CHARS
    ? trimmed.slice(0, RAIL_PREVIEW_MAX_CHARS)
    : trimmed;
}

// D4 — which founder message (index into `positions`, already-measured
// `top` offsets relative to the scroll container) is "current". At the
// bottom of the scroll → the last one. Otherwise → the last one whose top
// is at or above the viewport's vertical midpoint; if none qualify (every
// message is below the midpoint) → the first one. Pure: the caller
// (`useMessageRail.ts`) does the DOM measurement, this only picks the
// index — kept pure so a jsdom-free unit test can cover the edges (D12 i).
export function currentIndexOf(
  positions: number[],
  viewportMid: number,
  atBottom: boolean,
): number {
  if (positions.length === 0) return -1;
  if (atBottom) return positions.length - 1;
  for (let i = positions.length - 1; i >= 0; i--) {
    const pos = positions[i];
    if (pos !== undefined && pos <= viewportMid) return i;
  }
  return 0;
}
